import { Input, MMSwitchInput } from '@components'
import cn from '@integrations/classnames'
import { getRecurringFrequencyOptions } from '@utils'
import { normalizeLanguageCode } from '@utils/language'
import { selectCurrentLanguage, useAppSelector } from 'mmfintech-backend-api'
import { fixDateOnly, tr } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'

import styles from './RecurringToggleBox.module.scss'

interface Props {
  isRecurringOpen: boolean
  onToggle: () => void
  formValues: UseFormValuesOutput
}

function RecurringToggleBox({ isRecurringOpen, onToggle, formValues }: Props) {
  const selectedLanguage = normalizeLanguageCode(useAppSelector(selectCurrentLanguage))

  return (
    <div className={cn(styles['recurring-toggle-box'], 'recurring-toggle-box')} data-test='recurring-toggle-box'>
      <div className={cn(styles['recurring-toggle-box-header'], 'recurring-toggle-box-header')}>
        <div className={cn(styles['recurring-toggle-box-text'], 'recurring-toggle-box-text')}>
          <div className={cn(styles['recurring-toggle-box-title'], 'recurring-toggle-box-title')}>
            {tr('FRONTEND.BANKING.SEND_MONEY.RECURRING.TITLE', 'Schedule a recurring transfer')}
          </div>
          <div className={cn(styles['recurring-toggle-box-subtitle'], 'recurring-toggle-box-subtitle')}>
            {tr('FRONTEND.BANKING.SEND_MONEY.RECURRING.SUBTITLE', 'Repeat this transfer automatically')}
          </div>
        </div>
        <MMSwitchInput
          checked={isRecurringOpen}
          onChange={onToggle}
          data-test='recurring-toggle-switch'
          aria-label={tr('FRONTEND.BANKING.SEND_MONEY.RECURRING.TITLE', 'Schedule a recurring transfer')}
        />
      </div>

      {isRecurringOpen && (
        <div className={cn(styles['recurring-toggle-box-fields'], 'recurring-toggle-box-fields')}>
          <Input
            type='text'
            maxLength={50}
            label={tr('FRONTEND.BANKING.SEND_MONEY.RECURRING.PLAN_NAME', 'Recurring plan name')}
            {...formValues.registerInput('recurringPlanName')}
            autoComplete='off'
            data-test='recurring-plan-name'
          />

          <Input
            type='date'
            selectedLanguage={selectedLanguage}
            minDate={new Date()}
            label={tr('FRONTEND.BANKING.SEND_MONEY.RECURRING.START_DATE', 'Start date')}
            {...formValues.registerInput('startDate')}
            onChange={(name, value) => formValues.setValue(name, fixDateOnly(value))}
            data-test='recurring-start-date'
            overlayId='datepicker-portal'
            placement='bottom-start'
          />

          <Input
            type='select'
            options={getRecurringFrequencyOptions()}
            label={tr('FRONTEND.BANKING.SEND_MONEY.RECURRING.FREQUENCY', 'Frequency')}
            placeholder={tr('FRONTEND.BANKING.SEND_MONEY.RECURRING.SELECT_FREQUENCY', 'Select frequency')}
            {...formValues.registerInput('recurringStep')}
            data-test='recurring-frequency'
          />
        </div>
      )}
    </div>
  )
}

export default RecurringToggleBox
